import React, { forwardRef, useContext, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import {
    Button,
    Checkbox,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    Table as MUTable,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Toolbar,
    Typography,
} from '@material-ui/core';
import { lighten, makeStyles } from '@material-ui/core/styles';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import SortIcon from '@material-ui/icons/Sort';
import ViewColumnIcon from '@material-ui/icons/ViewColumn';
import { useFilters, useRowSelect, useSortBy, useTable } from 'react-table';

import { DataTableContext } from 'contexts/TableConfiguration';
import { downloadCSV } from 'shared/components/csvDownload';
import dayjs from 'utils/dayjsConfig';

import {
    CheckboxFilter,
    DateFilter,
    MultiSelectColumnFilter,
    SelectColumnFilter,
    TextColumnFilter,
} from './components/filters';
import { DefaultCell } from './components/tableCells';

import './dataTable.css';

const useToolbarStyles = makeStyles((theme) => ({
    root: {
        marginBottom: '10px!important',
    },
    highlight: {
        color: lighten(theme.palette.text.primary, 0.85),
        backgroundColor: theme.palette.primary.dark,
    },
    title: {
        flex: '1 1 100%',
    },
}));

function CustomizeColumnsModal({ close, columns, open, shiftColumn, toggleShowColumn }) {
    return (
        <Dialog
            open={open}
            onClose={() => close()}
            maxWidth="sm"
            fullWidth
            style={{ maxHeight: '750px', overlflowY: 'scroll' }}
        >
            <DialogTitle>Customize Columns</DialogTitle>
            <DialogContent>
                {columns.map((col) => (
                    <div
                        key={col.Header}
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            margin: '5px',
                            padding: '10px',
                            border: '1px solid lightgray',
                            borderRadius: '5px',
                        }}
                    >
                        <Checkbox
                            id={`${col.Header.replaceAll(' ', '')}_checkbox`}
                            color="primary"
                            checked={!col.hide}
                            onClick={() => {
                                toggleShowColumn(col.Header, !col.hide);
                            }}
                        />
                        {col.Header}
                    </div>
                ))}
            </DialogContent>
            <DialogActions>
                <Button
                    id="OK"
                    variant="outlined"
                    color="primary"
                    style={{ marginTop: '10px' }}
                    onClick={() => close()}
                >
                    OK
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function DuplicateRowModal({ close, columns, CustomFormComponent, onSuccess, open, row }) {
    // TBD: if no CustomFormComponent is provided, build a FormComponent based on table columns
    return (
        <Dialog
            open={open}
            onClose={() => {
                close();
            }}
            maxWidth="lg"
            fullWidth
        >
            <DialogContent>
                {CustomFormComponent &&
                    React.cloneElement(CustomFormComponent, {
                        initialValues: row || {},
                        onSuccess: () => {
                            onSuccess();
                            close();
                        },
                    })}
            </DialogContent>
        </Dialog>
    );
}

/**
 * Given a potentially nested path, and an object, return the value of the object at the path or null if it does not exist
 *
 * @example get("child.name", { child: {name: "Josh" }}) // "Josh"
 * @example get("child.name", { child: {} }) // null
 *
 * @param path string
 * @param obj object
 * @returns ?mixed
 */
function get(path, obj) {
    return path.split('.').reduce((child, path) => (child ? child[path] : null), obj);
}

const DataTable = forwardRef(
    (
        {
            bulkActions = [],
            columns,
            csvExport = false,
            csvExportAccessor = 'accessor',
            csvExportFileName = 'data',
            csvExportHeadersUseAccessor = false,
            customExport = null,
            customizableColumns = false,
            data,
            duplicateRowSettings = {},
            editable,
            expandable = false,
            ExpansionComponent,
            externalFilters = null,
            initialRowsPerPage = 10,
            initialSortBy = [],
            onRowClicked,
            preSelectedRowIDs = [],
            rowStyle,
            saveEdit,
            selectMultiple = false,
            showPagination = true,
            stickyHeader = false,
            tableContainerClass = '',
            tableContainerMaxHeight = null,
            tableName,
            title,
            toolbarActions = [],
        },
        ref,
    ) => {
        const tableContext = useContext(DataTableContext);
        let tableConfig = null;

        if (tableName) {
            tableConfig = tableContext.getColumnConfiguration(tableName);
        }

        const tableContainerStyles = {};
        if (tableContainerMaxHeight) tableContainerStyles.maxHeight = tableContainerMaxHeight;

        const textColumn = {
            Filter: TextColumnFilter,
            Cell: DefaultCell,
        };

        function multiSelectFilterFn(rows, id, filterValue) {
            const colAccessor = id[0];
            return rows.filter((row) => {
                const colText = row.original[colAccessor];
                if (Array.isArray(filterValue) && filterValue.length) {
                    return filterValue.includes(colText);
                }
                return true;
            });
        }

        const [internalColumns, setInternalColumns] = useState([]);

        // Build lookup from incoming columns prop (available on first render) so filter fns
        // can access editProps/filterValues even before internalColumns is populated by effects.
        const columnsByAccessor = useMemo(() => {
            const map = new Map();
            for (const c of columns || []) {
                if (c?.accessor) map.set(c.accessor, c);
                if (c?.id) map.set(c.id, c);
                if (c?.Header) map.set(c.Header, c);
            }
            return map;
        }, [columns]);

        function selectFilterFn(rows, id, filterValue) {
            const colAccessor = id[0];
            const col = columnsByAccessor.get(colAccessor);
            if (!col) {
                // If we can't find the column, don't blank the table.
                return rows;
            }

            const colOptions = col.editProps?.options || [];
            const q = Array.isArray(filterValue) ? null : String(filterValue ?? '').toLowerCase();

            return rows.filter((row) => {
                const value = row.original[colAccessor];

                let optArr;

                if (colOptions instanceof Function) {
                    optArr = colOptions(row.original);
                } else if (Array.isArray(colOptions)) {
                    optArr = colOptions.map((item) => {
                        if (typeof item === 'string') {
                            return { text: item, value: item };
                        }
                        return item;
                    });
                } else {
                    return false;
                }

                const opt = optArr.find((opt) => opt.value === value || opt.text === value);
                let colText;
                if (!opt || !opt.text) {
                    // option is not in dropdown, filter on the row's value
                    colText = row.original[colAccessor];
                } else {
                    // option is in dropdown, we want to filter by the option's text since
                    // value could be something other than what is displayed (an id)
                    colText = opt.text;
                }

                if (typeof colText !== 'string') {
                    colText = `${colText}`;
                }

                if (Array.isArray(filterValue)) {
                    if (filterValue.length) {
                        return filterValue.includes(colText);
                    }
                    return true;
                }
                return colText.toLowerCase().includes(q);
            });
        }

        function checkboxFilterFn(rows, id, filterValue) {
            if (filterValue === 'all' || filterValue === '') {
                return rows;
            }

            const colAccessor = id[0];
            return rows.filter((row) => {
                const value = get(colAccessor, row.original);
                return value === filterValue;
            });
        }

        function dateFilterFn(rows, id, filterValue) {
            const type = filterValue.filterType;

            if (type === 'all') {
                return rows;
            }

            const colAccessor = id[0];
            const date1 = dayjs(filterValue.date1);
            let date2 = '';
            if (filterValue.date2) {
                date2 = dayjs(filterValue.date2);
            }

            return rows.filter((row) => {
                const value = row.original[colAccessor];
                if (!value) {
                    return false;
                }

                const dateValue = dayjs(value);
                if (type === 'equals') {
                    return dateValue.format('YYYY/MM/DD') === date1.format('YYYY/MM/DD');
                }
                if (type === 'greaterThan') {
                    return dateValue.isAfter(date1, 'day');
                }
                if (type === 'lessThan') {
                    return dateValue.isBefore(date1, 'day');
                }
                if (type === 'between') {
                    return dateValue.isBetween(date1, date2, 'day', '[]');
                }
            });
        }

        function arrayFilterFn(rows, id, filterValue) {
            const colAccessor = id[0];
            const col = columnsByAccessor.get(colAccessor);
            if (!col) {
                // If we can't find the column, don't blank the table.
                return rows;
            }

            if (!col.filterValues || !(col.filterValues instanceof Function)) {
                return rows;
            }

            const q = String(filterValue ?? '').toLowerCase();
            return rows.filter((row) => {
                const values = col.filterValues(row);
                return (values || []).some((item) =>
                    String(item ?? '')
                        .toLowerCase()
                        .includes(q),
                );
            });
        }

        // Collect all primitive values (string/number/boolean) from a nested object/array
        function collectPrimitiveStrings(input, collected = [], seen = new Set(), depth = 0) {
            if (input == null) return collected;
            if (depth > 4) return collected; // guard against deep structures

            const type = typeof input;
            if (type === 'string' || type === 'number' || type === 'boolean') {
                collected.push(String(input));
                return collected;
            }

            if (type === 'object') {
                if (seen.has(input)) return collected; // avoid cycles
                seen.add(input);

                if (Array.isArray(input)) {
                    for (const item of input) {
                        collectPrimitiveStrings(item, collected, seen, depth + 1);
                    }
                } else {
                    for (const key of Object.keys(input)) {
                        collectPrimitiveStrings(input[key], collected, seen, depth + 1);
                    }
                }
            }

            return collected;
        }

        function fuzzyFilterFn(rows, id, filterValue) {
            const query = String(filterValue == null ? '' : filterValue).trim();
            if (query.length === 0) return rows;

            const colId = id[0];
            const q = query.toLowerCase();

            return rows.filter((row) => {
                const col = columnsByAccessor.get(colId);

                const candidateValues = [];
                if (col && typeof col.filterValues === 'function') {
                    const values = col.filterValues(row) || [];
                    candidateValues.push(...values);
                } else {
                    const fallbackValue = row.original && (row.original[colId] ?? row.values?.[colId]);
                    candidateValues.push(fallbackValue);
                }

                for (const val of candidateValues) {
                    const s = String(val == null ? '' : val).toLowerCase();
                    if (s.indexOf(q) !== -1) {
                        return true;
                    }
                }

                // Only do the expensive "scan whole row" fallback if needed.
                const deepValues = collectPrimitiveStrings(row.original || {});
                for (const val of deepValues) {
                    const s = String(val == null ? '' : val).toLowerCase();
                    if (s.indexOf(q) !== -1) {
                        return true;
                    }
                }
                return false;
            });
        }

        const filterTypes = useMemo(
            () => ({
                select: selectFilterFn,
                array: arrayFilterFn,
                checkbox: checkboxFilterFn,
                date: dateFilterFn,
                multiSelect: multiSelectFilterFn,
                fuzzy: fuzzyFilterFn,
            }),
            [columnsByAccessor],
        );

        useEffect(() => {
            const cols = columns.map((item) => {
                const colItem = { ...item };
                colItem.type = item.type || 'string';
                colItem.editable = item.editable || false;
                colItem.width = item.width || '100px';
                colItem.sortable = item.hasOwnProperty('sortable') ? item.sortable : true;
                colItem.useFilter = item.hasOwnProperty('useFilter') ? item.useFilter : true;
                if (
                    !!externalFilters &&
                    externalFilters.hasOwnProperty(item.accessor) &&
                    !!externalFilters[item.accessor]
                ) {
                    colItem.filterDisabled = true;
                }
                if (editable) {
                    colItem.editProps = item.editProps ? item.editProps : { type: 'input', inputType: 'text' };
                }

                colItem.hide = item.hasOwnProperty('hide') ? item.hide : false;
                if (tableConfig) {
                    const c = tableConfig.find((col) => col.Header === item.Header);
                    if (c) {
                        colItem.hide = c.hide;
                    }
                }

                if (colItem.filterType) {
                    colItem.filter = colItem.filterType;

                    if (colItem.filterType === 'select') {
                        colItem.Filter = SelectColumnFilter;
                    } else if (colItem.filterType === 'multiSelect') {
                        colItem.Filter = MultiSelectColumnFilter;
                    }
                } else {
                    if (colItem.editProps?.type === 'select') {
                        // colItem.Filter = SelectColumnFilter;
                        colItem.filter = 'select';
                    }

                    if (colItem.filterValues) {
                        // these are the values that the filter will match
                        colItem.filter = 'array';
                    }

                    if (colItem.type === 'checkbox') {
                        colItem.Filter = CheckboxFilter;
                        colItem.filter = 'checkbox';
                    }

                    if (
                        colItem.editProps?.type === 'date' ||
                        colItem.editProps?.inputType === 'date' ||
                        colItem.type === 'date'
                    ) {
                        colItem.Filter = DateFilter;
                        colItem.filter = 'date';
                    }
                }

                return colItem;
            });

            if (duplicateRowSettings.enable) {
                cols.unshift({
                    Header: 'Duplicate',
                    editable: false,
                    Cell: ({ row }) => (
                        <Button
                            id={`Duplicate_${row.id}`}
                            onClick={async () => {
                                if (
                                    duplicateRowSettings.fetchAdditionalData &&
                                    duplicateRowSettings.fetchAdditionalData instanceof Function
                                ) {
                                    const data = await duplicateRowSettings.fetchAdditionalData(row.original);
                                    row.original.additionalData = data;
                                }
                                setSelectedDuplicateRow(row.original);
                                setDuplicateRowModalOpen(true);
                            }}
                        >
                            Duplicate
                        </Button>
                    ),
                });
            }
            setInternalColumns(cols);
        }, [columns, externalFilters]);

        const sortTypes = useMemo(
            () => ({
                datetimeString: (rowA, rowB, id) => {
                    const rowAValue = new Date(rowA.values[id]);
                    const rowBValue = new Date(rowB.values[id]);
                    return rowAValue - rowBValue;
                },
            }),
            [],
        );

        const sortBy = useMemo(() => initialSortBy, [initialSortBy]);

        const tableState = useTable(
            {
                columns: internalColumns.filter((item) => !item.hide),
                data,
                defaultColumn: textColumn,
                filterTypes,
                sortTypes,
                autoResetPage: false,
                autoResetSortBy: false,
                autoResetFilters: false,
                autoResetRowState: false,
                initialState: {
                    hiddenColumns: columns.filter((item) => item.hiddenColumn).map((item) => item.accessor),
                    sortBy,
                },
            },
            useFilters,
            useSortBy,
            useRowSelect,
        );

        const { getTableProps, headerGroups, prepareRow, rows } = tableState;

        const [page, setPage] = useState(0);
        const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);
        const [expandedRow, setExpandedRow] = useState(null);
        const [selectedRows, setSelectedRows] = useState([]);
        const [customizeColumnsModalOpen, setCustomizeColumnsModalOpen] = useState(false);
        const [duplicateRowModalOpen, setDuplicateRowModalOpen] = useState(false);
        const [selectedDuplicateRow, setSelectedDuplicateRow] = useState(null);

        useEffect(() => {
            setPage(0);
            setExpandedRow(null);
            setSelectedRows([]);
        }, [tableState.state.filters]);

        // Listen for input changes outside
        useEffect(() => {
            if (externalFilters) {
                Object.keys(externalFilters).forEach((key) => {
                    tableState.setFilter(key, externalFilters[key]);
                });
            }
        }, [externalFilters]);

        const handleChangePage = (event, page) => {
            // setSelectedRows([]);
            setExpandedRow(null);
            setPage(page);
        };

        const handleChangeRowsPerPage = (event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
        };

        const handleRowClick = (row, index) => {
            if (onRowClicked && onRowClicked instanceof Function) {
                onRowClicked(row);
            }

            if (!expandable) {
                return;
            }

            if (expandedRow === index) {
                setExpandedRow(null);
            } else {
                setExpandedRow(index);
            }
        };

        const exportData = () => {
            // Helper function to properly escape CSV values
            const escapeCSVValue = (value) => {
                if (value === null || value === undefined) {
                    return '';
                }
                // Convert to string and escape quotes by doubling them
                const stringValue = String(value);
                // If value contains comma, quote, or newline, wrap in quotes and escape internal quotes
                if (
                    stringValue.includes(',') ||
                    stringValue.includes('"') ||
                    stringValue.includes('\n') ||
                    stringValue.includes('\r')
                ) {
                    return `"${stringValue.replace(/"/g, '""')}"`;
                }
                return stringValue;
            };

            const headerContent = internalColumns
                .filter((col) => col.hasOwnProperty(csvExportAccessor))
                .map((col) => {
                    const headerValue = csvExportHeadersUseAccessor ? get(csvExportAccessor, col) : col.Header;
                    return escapeCSVValue(headerValue);
                })
                .join(',');

            let csvContent = `${headerContent}\r\n`;

            for (const row of rows) {
                const rowValues = [];
                for (const col of internalColumns) {
                    if (!col.hasOwnProperty(csvExportAccessor)) {
                        continue;
                    }

                    const rowColAccessor = get(csvExportAccessor, col);
                    const cellContent = get(rowColAccessor, row.original);
                    rowValues.push(escapeCSVValue(cellContent));
                }
                csvContent += `${rowValues.join(',')}\r\n`;
            }

            const today = dayjs();
            const filename = `${csvExportFileName}${today.format('_YYYY_MM_DD_HH_mm')}.csv`;

            downloadCSV(csvContent, filename);
        };

        const toggleShowColumn = (colHeader, hide) => {
            if (!tableName) {
                return;
            }

            const cols = internalColumns.map((item) => {
                if (item.Header === colHeader) {
                    item.hide = hide;
                }
                return item;
            });

            setInternalColumns(cols);

            // save new configuration
            const tableConfiguration = cols.map((item) => ({
                Header: item.Header,
                hide: item.hasOwnProperty('hide') ? item.hide : false,
            }));
            tableContext.setColumnConfiguration(tableName, tableConfiguration);
        };

        const classes = useToolbarStyles();

        useEffect(() => {
            if (selectMultiple) {
                setSelectedRows(preSelectedRowIDs);
            } else {
                setSelectedRows([]);
            }
        }, [data]);

        // Expose methods or state through the ref
        useImperativeHandle(
            ref,
            () => ({
                getSelectedRows: () => selectedRows,
            }),
            [selectedRows],
        );

        return (
            <>
                {tableName && customizableColumns && (
                    <CustomizeColumnsModal
                        columns={internalColumns}
                        open={customizeColumnsModalOpen}
                        close={() => {
                            setCustomizeColumnsModalOpen(false);
                        }}
                        toggleShowColumn={toggleShowColumn}
                    />
                )}
                <DuplicateRowModal
                    columns={internalColumns}
                    open={duplicateRowModalOpen}
                    row={selectedDuplicateRow}
                    CustomFormComponent={duplicateRowSettings.CustomFormComponent}
                    onSuccess={duplicateRowSettings.onSuccess}
                    close={() => {
                        setSelectedDuplicateRow(null);
                        setDuplicateRowModalOpen(false);
                    }}
                />
                <Toolbar className={`${classes.root} ${selectedRows.length > 0 ? classes.highlight : ''}`}>
                    {selectedRows.length > 0 && (
                        <Grid justify="space-between" container spacing={10}>
                            <Grid item>
                                <div>
                                    {selectedRows.length === rowsPerPage && rows.length > selectedRows.length ? (
                                        <div>
                                            All {selectedRows.length} items on page selected.
                                            <span
                                                style={{ fontWeight: 'bold', cursor: 'pointer' }}
                                                onClick={() => {
                                                    const allIDs = rows.map((item) => item.original.id);
                                                    setSelectedRows(allIDs);
                                                }}
                                            >
                                                {' '}
                                                Select All {rows.length} in results?
                                            </span>
                                        </div>
                                    ) : (
                                        `${selectedRows.length} items selected`
                                    )}
                                </div>
                            </Grid>
                            <Grid item>
                                {bulkActions.map((item) => (
                                    <Button
                                        variant="outlined"
                                        style={{ margin: '0px 8px' }}
                                        id={item.name}
                                        key={item.name}
                                        disabled={item.hasOwnProperty('disabled') ? item.disabled : false}
                                        color="inherit"
                                        onClick={async () => {
                                            const res = await item.action(selectedRows);
                                            if (res === true) {
                                                setSelectedRows([]);
                                            }
                                        }}
                                    >
                                        {item.name}
                                    </Button>
                                ))}
                            </Grid>
                        </Grid>
                    )}
                    {selectedRows.length === 0 && (
                        <Grid justify="space-between" container spacing={10}>
                            <Grid item>
                                <Typography variant="h5">{title}</Typography>
                            </Grid>
                            <Grid item>
                                <div style={{ textAlign: 'left' }}>
                                    {!!externalFilters &&
                                        Object.keys(externalFilters)
                                            .filter((key) => !!externalFilters[key])
                                            .map((key) => {
                                                let filtered = '';
                                                if (Array.isArray(externalFilters[key])) {
                                                    if (externalFilters[key].length === 1) {
                                                        filtered = externalFilters[key][0];
                                                    } else {
                                                        filtered = `${externalFilters[key][0]} - ${
                                                            externalFilters[key][externalFilters[key].length - 1]
                                                        }`;
                                                    }
                                                } else {
                                                    filtered = externalFilters[key];
                                                }
                                                return (
                                                    <span key={key}>
                                                        <span style={{ fontWeight: 'bold' }}>{key.toUpperCase()}:</span>{' '}
                                                        {filtered} &nbsp;&nbsp;
                                                    </span>
                                                );
                                            })}
                                </div>
                            </Grid>
                            <Grid item>
                                {toolbarActions.map((item, index) => (
                                    <ToolbarButton
                                        key={index}
                                        item={item}
                                        setSelectedRows={setSelectedRows}
                                        selectedRows={selectedRows}
                                    />
                                ))}
                                {customizableColumns && tableName && (
                                    <Button
                                        id="CustomizeColumns"
                                        color="secondary"
                                        style={{ marginLeft: '20px' }}
                                        variant="contained"
                                        onClick={() => {
                                            setCustomizeColumnsModalOpen(true);
                                        }}
                                    >
                                        <ViewColumnIcon />
                                    </Button>
                                )}
                                {csvExport && (
                                    <Button
                                        color="secondary"
                                        style={{ marginLeft: '20px' }}
                                        variant="contained"
                                        onClick={() => {
                                            if (customExport && customExport instanceof Function) {
                                                customExport(rows);
                                            } else {
                                                exportData();
                                            }
                                        }}
                                    >
                                        Export
                                    </Button>
                                )}
                            </Grid>
                        </Grid>
                    )}
                </Toolbar>
                <TableContainer style={tableContainerStyles} className={tableContainerClass}>
                    <div style={{ width: '100%' }} data-testid="datatable">
                        <MUTable {...getTableProps()} stickyHeader={stickyHeader} className="dt-table" size="small">
                            <TableHead>
                                {headerGroups.map((headerGroup) => {
                                    const headerGroupProps = headerGroup.getHeaderGroupProps();
                                    return (
                                        <TableRow
                                            key={headerGroupProps.key}
                                            {...Object.fromEntries(
                                                Object.entries(headerGroupProps).filter(([key]) => key !== 'key'),
                                            )}
                                        >
                                            {selectMultiple === true && (
                                                <TableCell>
                                                    <Checkbox
                                                        id="TableHeader_checkbox"
                                                        color="primary"
                                                        checked={selectedRows.length > 0}
                                                        onChange={(e) => {
                                                            if (e.target.checked === true) {
                                                                const allIDs = rows
                                                                    .slice(
                                                                        page * rowsPerPage,
                                                                        page * rowsPerPage + rowsPerPage,
                                                                    )
                                                                    .filter(
                                                                        (row) =>
                                                                            (Object.keys(row.original).includes(
                                                                                'canUserSelectBulkAction',
                                                                            ) &&
                                                                                row.original.canUserSelectBulkAction) ||
                                                                            !(
                                                                                'canUserSelectBulkAction' in
                                                                                row.original
                                                                            ),
                                                                    )
                                                                    .map((item) => item.original.id);
                                                                setSelectedRows(allIDs);
                                                            } else if (e.target.checked === false) {
                                                                setSelectedRows([]);
                                                            }
                                                        }}
                                                    />
                                                </TableCell>
                                            )}
                                            {headerGroup.headers.map((column) => {
                                                const columnProps = column.getHeaderProps();
                                                return (
                                                    <TableCell
                                                        style={{ minWidth: column.width }}
                                                        key={columnProps.key}
                                                        {...Object.fromEntries(
                                                            Object.entries(columnProps).filter(
                                                                ([key]) => key !== 'key',
                                                            ),
                                                        )}
                                                    >
                                                        <div {...column.getSortByToggleProps()}>
                                                            <span>{column.render('Header')}</span>
                                                            {column.sortable !== false && (
                                                                <div
                                                                    id={`${column.Header.replaceAll(
                                                                        ' ',
                                                                        '',
                                                                    )}_header_sorter`}
                                                                    className="dt-header-sort-container"
                                                                >
                                                                    <span className="dt-header-sort">
                                                                        {column.isSorted ? (
                                                                            column.isSortedDesc ? (
                                                                                <KeyboardArrowDownIcon className="dt-header-icon" />
                                                                            ) : (
                                                                                <KeyboardArrowUpIcon className="dt-header-icon" />
                                                                            )
                                                                        ) : (
                                                                            <SortIcon className="dt-header-icon" />
                                                                        )}
                                                                    </span>
                                                                </div>
                                                            )}
                                                            {column.useFilter !== false && (
                                                                <div>
                                                                    <span>{column.render('Filter')}</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </TableCell>
                                                );
                                            })}
                                        </TableRow>
                                    );
                                })}
                            </TableHead>
                            <TableBody>
                                {rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, index) => {
                                    prepareRow(row);
                                    let styles = {};
                                    if (rowStyle && rowStyle instanceof Function) {
                                        styles = rowStyle(row.original);
                                    }
                                    return (
                                        <React.Fragment key={index}>
                                            <Row
                                                key={`row-${index}`}
                                                styles={styles}
                                                expandedRow={expandedRow}
                                                index={index}
                                                handleRowClick={handleRowClick}
                                                rowProps={{ ...row.getRowProps(), key: `row-${index}` }}
                                                selectMultiple={selectMultiple}
                                                selectedRows={selectedRows}
                                                row={row}
                                                setSelectedRows={setSelectedRows}
                                                editable={editable}
                                                saveEdit={saveEdit}
                                            />
                                            {expandable && (
                                                <TableRow>
                                                    <TableCell
                                                        colSpan={internalColumns.filter((item) => !item.hide).length}
                                                    >
                                                        <Collapse
                                                            unmountOnExit
                                                            in={expandedRow === index}
                                                            key={`expandable-${index}`}
                                                        >
                                                            {React.cloneElement(ExpansionComponent, {
                                                                parentRow: row,
                                                                setExpandedRow,
                                                            })}
                                                        </Collapse>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </TableBody>
                        </MUTable>
                    </div>
                </TableContainer>
                {showPagination && (
                    <TablePagination
                        component="div"
                        rowsPerPageOptions={[5, 10, 25, 50, 100]}
                        count={rows.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onChangePage={handleChangePage}
                        onChangeRowsPerPage={handleChangeRowsPerPage}
                    />
                )}
            </>
        );
    },
);

function ToolbarButton({ item, selectedRows, setSelectedRows }) {
    const [isRunning, setIsRunning] = useState(false);
    const disabled = Boolean(item.disabled) || isRunning;

    return (
        <Button
            id={`${item.name.replaceAll(' ', '')}`}
            color={item.color ?? 'secondary'}
            style={{ marginLeft: '20px' }}
            disabled={disabled}
            key={item.name}
            variant="contained"
            onClick={async () => {
                setIsRunning(true);
                try {
                    const res = await item.action(selectedRows);
                    if (res === true) {
                        setSelectedRows([]);
                    }
                } finally {
                    setIsRunning(false);
                }
            }}
        >
            {item.name}
        </Button>
    );
}

function Row({
    editable,
    expandedRow,
    handleRowClick,
    index,
    row,
    rowProps,
    saveEdit,
    selectedRows,
    selectMultiple,
    setSelectedRows,
    styles,
}) {
    const handleSaveEdit = async (obj) => {
        let res = true;
        if (saveEdit && saveEdit instanceof Function) {
            res = await saveEdit(obj);
        }
        return res;
    };

    return (
        <TableRow
            key={rowProps.key}
            style={styles}
            className={`${expandedRow === index ? 'selectedRow' : ''}`}
            onClick={() => {
                handleRowClick(row, index);
            }}
            {...Object.fromEntries(Object.entries(rowProps).filter(([key]) => key !== 'key'))}
        >
            {selectMultiple === true && (
                <TableCell className="dt-table-cell-class">
                    <Checkbox
                        disabled={
                            Object.keys(row.original).includes('canUserSelectBulkAction')
                                ? !row.original.canUserSelectBulkAction
                                : false
                        }
                        id={`RowCheckbox_${index}`}
                        checked={selectedRows.includes(row.original.id)}
                        onChange={(event) => {
                            const selected = JSON.parse(JSON.stringify(selectedRows));
                            const value = event.target.checked;
                            if (value === true) {
                                if (!selected.includes(row.original.id)) {
                                    selected.push(row.original.id);
                                }
                            } else if (value === false) {
                                const ind = selected.indexOf(row.original.id);
                                if (ind > -1) {
                                    selected.splice(ind, 1);
                                }
                            }
                            setSelectedRows(selected);
                        }}
                        color="primary"
                    />
                </TableCell>
            )}
            {row.cells.map((cell) => {
                const isCellEditable = function (cell, editable) {
                    if (editable !== true) {
                        return false;
                    }
                    if (cell.column.editable === false) {
                        return false;
                    }
                    if (cell.column.editable instanceof Function) {
                        return cell.column.editable(cell.row);
                    }
                    return true;
                };
                const cellEditable = isCellEditable(cell, editable);
                const cellProps = cell.getCellProps();
                return (
                    <TableCell
                        key={cellProps.key}
                        className="dt-table-cell-class"
                        {...Object.fromEntries(Object.entries(cellProps).filter(([key]) => key !== 'key'))}
                    >
                        {cell.render('Cell', { editable: cellEditable, saveEdit: handleSaveEdit })}
                    </TableCell>
                );
            })}
        </TableRow>
    );
}

export { DataTable };
